import { Injectable, NgZone } from '@angular/core';
import { Chart, ChartConfiguration } from 'chart.js';
import {
  buildStackedBarExportConfig,
  cloneChartConfiguration,
  resetSharedBarChartOptions,
} from './chart-export-config.util';

export interface ChartCaptureTarget {
  selector: string;
  chart: { canvas?: HTMLCanvasElement; toBase64Image?: (type?: string, quality?: number) => string };
}

export interface ChartSnapshotOptions {
  /** Re-render the chart at PDF page width/height with larger labels before capture. */
  fullPage?: boolean;
  /** Override export chart width (CSS px). */
  chartExportWidthPx?: number;
  /** Override export chart height (CSS px); default depends on chart orientation. */
  chartExportHeightPx?: number;
  /** Chart.js device pixel ratio for export (default 3). */
  chartDevicePixelRatio?: number;
}

type ExportableChart = ChartCaptureTarget['chart'] & {
  options?: {
    responsive?: boolean;
    animation?: boolean | { duration?: number };
    maintainAspectRatio?: boolean;
    devicePixelRatio?: number;
    indexAxis?: string;
    datasets?: { bar?: Record<string, unknown> };
    scales?: Record<string, { ticks?: { font?: unknown }; beginAtZero?: boolean }>;
    layout?: { padding?: { top?: number; right?: number; bottom?: number; left?: number } };
    plugins?: {
      legend?: { labels?: { font?: unknown } };
      datalabels?: { font?: unknown; display?: boolean | ((...args: unknown[]) => boolean) };
    };
  };
  data?: { datasets?: Array<{ barThickness?: number; maxBarThickness?: number }>; labels?: unknown[] };
  resize?: () => void;
  update?: (mode?: string) => void;
  /** chartjs-plugin-zoom — resets any active zoom back to default view. */
  resetZoom?: () => void;
};

/** Match landscape PDF content width at 2x capture scale (762pt * 2). */
const PDF_EXPORT_CAPTURE_WIDTH_PX = 1524;
/** Chart export size (CSS px) — matches landscape PDF content width at 2x pixel ratio. */
const CHART_EXPORT_WIDTH_PX = PDF_EXPORT_CAPTURE_WIDTH_PX;
const CHART_EXPORT_HEIGHT_PX = 900;
/** Assumed on-screen width for consistent export typography (not the browser viewport). */
const CHART_EXPORT_BASELINE_WIDTH_PX = 640;
const CHART_EXPORT_BASE_FONT_SIZE_PX = 11;
/** Legend labels in exported charts (kept in step with axis tick size). */
const CHART_EXPORT_LEGEND_BASE_FONT_SIZE_PX = 11;
/** Charts are rendered at export width; tick fonts must not scale with that width. */
const CHART_EXPORT_MAX_TICK_FONT_SIZE_PX = 12;
const CHART_EXPORT_MAX_TYPOGRAPHY_SCALE = 2.75;
/** Match statistics-pdf-export PDF_PIXEL_RATIO so chart pixels align with PDF embed. */
const CHART_EXPORT_DEVICE_PIXEL_RATIO = 2;

@Injectable({
  providedIn: 'root',
})
export class StatisticsExportCaptureService {
  private offscreenExportCounter = 0;

  constructor(private ngZone: NgZone) {}

  captureChart(
    chart: ChartCaptureTarget['chart'] | undefined,
    backgroundColor = '#ffffff'
  ): string | null {
    if (!chart?.toBase64Image) {
      return null;
    }

    const sourceCanvas = chart.canvas;
    if (!sourceCanvas || typeof document === 'undefined') {
      return chart.toBase64Image('image/png', 1);
    }

    return this.renderCanvasWithBackground(sourceCanvas, backgroundColor);
  }

  /** Remove inline export layout from a chart canvas and its container. */
  clearChartExportLayoutStyles(canvasId: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
    if (!canvas) {
      return;
    }

    const container = canvas.parentElement;
    if (container) {
      const styleProps = [
        'width',
        'height',
        'minWidth',
        'maxWidth',
        'boxSizing',
        'background',
        'backgroundColor',
        'position',
        'left',
        'top',
        'zIndex',
        'visibility',
        'padding',
        'paddingBottom',
        'marginTop',
        'overflow',
      ] as const;
      for (const prop of styleProps) {
        container.style[prop] = '';
      }
    }

    canvas.style.width = '';
    canvas.style.height = '';
    canvas.removeAttribute('width');
    canvas.removeAttribute('height');
  }

  /**
   * Reset Chart.js options that applyChartExportLayout may leave on shared singleton configs.
   * Call before creating a new Chart from chart-configs.shared.ts after PDF capture.
   */
  resetSharedBarChartOptions(config: ChartConfiguration<'bar'>): void {
    resetSharedBarChartOptions(config);
  }


  /**
   * Release a canvas's backing store immediately (width/height = 0) instead of
   * waiting for GC. Export capture canvases are large (2–4x scale); freeing each
   * as soon as it is encoded keeps peak memory down during a multi-widget export.
   */
  private freeCanvas(canvas: HTMLCanvasElement | null | undefined): void {
    if (!canvas) {
      return;
    }
    canvas.width = 0;
    canvas.height = 0;
  }


  /**
   * Build a ChartConfiguration for an off-screen stacked-bar export.
   * Shared by the four bar-chart widgets (alerts/active-users per day/hour) so
   * each component does not need its own buildPdfExportChartConfig() copy.
   */
  buildStackedBarExportConfig(
    items: Array<{ label: string; data: (number | null)[] }>,
    labels: string[],
    baseConfig: ChartConfiguration<'bar'>,
    barThickness: number,
    colorPalette: string[]
  ): ChartConfiguration<'bar'> {
    return buildStackedBarExportConfig(items, labels, baseConfig, barThickness, colorPalette);
  }

  /**
   * Render a chart in a temporary off-screen canvas for PDF capture.
   * The live on-page chart is never resized or mutated.
   */
  async buildOffscreenChartSnapshot(
    chartConfig: ChartConfiguration,
    slug: string,
    titleKey: string,
    options: ChartSnapshotOptions = {}
  ): Promise<import('./statistics-export.models').StatisticsExportSnapshot | null> {
    if (typeof document === 'undefined') {
      return null;
    }

    // Resolve target dimensions up-front so the container is already at export size when
    // Chart.js initializes. A zero-size initial container causes Chart.js to compute
    // scales and bar layout at 0px width — subsequent resize() calls may not fully
    // recover bar positioning, producing the "floating bars above the x-axis" symptom.
    const exportWidthPx = options.chartExportWidthPx ?? (options.fullPage ? CHART_EXPORT_WIDTH_PX : 400);
    const exportHeightPx = options.chartExportHeightPx ?? (options.fullPage ? CHART_EXPORT_HEIGHT_PX : 300);

    const canvasId = `statistics-export-${slug}-${++this.offscreenExportCounter}`;
    const container = document.createElement('div');
    const canvas = document.createElement('canvas');
    canvas.id = canvasId;
    container.appendChild(canvas);
    container.style.position = 'fixed';
    container.style.left = '-12000px';
    container.style.top = '0';
    container.style.width = `${exportWidthPx}px`;
    container.style.height = `${exportHeightPx}px`;
    container.style.visibility = 'hidden';
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);

    // Clone config with responsive:false and animation:false so the chart initialises
    // at the fixed container size immediately without waiting for a resize event.
    const clonedConfig = this.cloneChartConfiguration(chartConfig);
    if (clonedConfig.options) {
      clonedConfig.options.responsive = false;
      clonedConfig.options.maintainAspectRatio = false;
      clonedConfig.options.animation = false;
    }

    let chart: Chart | undefined;
    try {
      chart = new Chart(canvasId, clonedConfig);
      return await this.buildChartSnapshot(chart, slug, titleKey, options);
    } finally {
      try {
        chart?.destroy();
      } catch {
        // ignore destroy failures on ephemeral export charts
      }
      Chart.getChart(canvas)?.destroy();
      container.remove();
    }
  }

  /** Deep-enough copy so export layout never mutates the caller's config object. */
  cloneChartConfiguration(config: ChartConfiguration): ChartConfiguration {
    return cloneChartConfiguration(config);
  }

  async buildChartSnapshot(
    chart: ChartCaptureTarget['chart'] | undefined,
    slug: string,
    titleKey: string,
    options: ChartSnapshotOptions = {}
  ): Promise<import('./statistics-export.models').StatisticsExportSnapshot | null> {
    const exportWidthPx =
      options.chartExportWidthPx ??
      (options.fullPage ? CHART_EXPORT_WIDTH_PX : undefined);
    const exportHeightPx =
      options.chartExportHeightPx ??
      (options.fullPage ? CHART_EXPORT_HEIGHT_PX : undefined);
    const shouldLayoutChart = chart && exportWidthPx && exportHeightPx;
    const restoreChartLayout = shouldLayoutChart
      ? this.applyChartExportLayout(chart, exportWidthPx, exportHeightPx, {
          devicePixelRatio: options.chartDevicePixelRatio ?? CHART_EXPORT_DEVICE_PIXEL_RATIO,
        })
      : null;

    try {
      if (restoreChartLayout) {
        // Reset any active zoom so the export always shows the default (unzoomed) view.
        (chart as ExportableChart)?.resetZoom?.();
        // Wait for the browser to apply the inline CSS sizing we just set, THEN ask
        // Chart.js to recompute its layout against the new container size and redraw.
        // Without this second resize/update pair, Chart.js may have run chart.resize()
        // synchronously inside applyChartExportLayout against the still-stale container
        // dimensions (CSS layout had not yet been applied), leaving the chart drawn at
        // the on-screen size while the canvas drawing-buffer is now the much larger
        // export size — the captured image then shows bars only in the upper portion
        // of the chart area (the "floating above x-axis" symptom on the active-users
        // and alerts per-day/per-hour vertical bar charts).
        await this.waitForLayout();
        const liveChart = chart as ExportableChart & { canvas?: HTMLCanvasElement | null };
        if (liveChart?.canvas?.isConnected && liveChart.canvas?.parentElement) {
          try {
            liveChart.resize?.();
            liveChart.update?.('none');
          } catch (err) {
            console.warn('[statistics-export-capture] pre-capture chart.resize skipped:', err);
          }
        }
        await this.waitForChartPaint(chart);
      }
      const chartType = (chart as { config?: { type?: string } })?.config?.type;
      const isCircularChart = chartType === 'pie' || chartType === 'doughnut';
      const imageDataUrl = this.captureChart(
        chart,
        isCircularChart ? '#e5e8ef' : '#ffffff'
      );
      if (!imageDataUrl) {
        return null;
      }
      return {
        slug,
        titleKey,
        imageDataUrl,
        scaleToFullPage: options.fullPage === true,
      };
    } finally {
      const restoredCanvas = restoreChartLayout?.();
      if (restoredCanvas) {
        // Apply restored CSS first, then resize the chart instance that is CURRENTLY
        // registered on this canvas (not the closed-over `chart` reference, which may
        // already have been destroyed/replaced by the host component).
        await this.waitForLayout();
        const liveChart = Chart.getChart(restoredCanvas) as ExportableChart | undefined;
        if (liveChart?.canvas?.isConnected && liveChart.canvas?.parentElement) {
          try {
            liveChart.resize();
            liveChart.update('none');
          } catch (err) {
            console.warn('[statistics-export-capture] post-restore chart.resize skipped:', err);
          }
        }
      }
    }
  }

  private applyChartExportLayout(
    chart: ExportableChart,
    exportWidthPx: number,
    exportHeightPx: number,
    layoutOptions: { devicePixelRatio?: number } = {}
  ): (() => HTMLCanvasElement | void) | (() => void) {
    if (!chart.canvas || typeof chart.resize !== 'function' || typeof chart.update !== 'function') {
      return () => undefined;
    }

    const canvas = chart.canvas;
    const container = canvas.parentElement as HTMLElement | null;
    if (!container) {
      return () => undefined;
    }

    const chartType = (chart as { config?: { type?: string } }).config?.type;
    const isCircularChart = chartType === 'pie' || chartType === 'doughnut';
    let layoutWidthPx = exportWidthPx;
    let layoutHeightPx = exportHeightPx;
    if (isCircularChart) {
      const squareSize = Math.min(exportWidthPx, exportHeightPx);
      layoutWidthPx = squareSize;
      layoutHeightPx = squareSize;
    }

    const isHorizontalBarChart = chart.options?.indexAxis === 'y';

    const parent = container.parentElement;
    // Only detach circular charts. Horizontal bars must stay in-document so the canvas
    // is painted (off-screen detach at -12000px often yields labels/datalabels without bars).
    const shouldDetachForExport = typeof document !== 'undefined' && isCircularChart;
    const exportAnchor =
      shouldDetachForExport && parent
        ? document.createComment('statistics-chart-export-anchor')
        : null;

    // Resolved before `saved` so the restore closure can reference it.
    const exportCard = isHorizontalBarChart
      ? (container.closest('.card') as HTMLElement | null)
      : null;

    const saved = {
      parentElement: parent,
      containerWidth: container.style.width,
      containerHeight: container.style.height,
      containerMinWidth: container.style.minWidth,
      containerMaxWidth: container.style.maxWidth,
      containerBackground: container.style.background,
      containerBackgroundColor: container.style.backgroundColor,
      containerPosition: container.style.position,
      containerLeft: container.style.left,
      containerTop: container.style.top,
      containerZIndex: container.style.zIndex,
      containerVisibility: container.style.visibility,
      containerPadding: container.style.padding,
      containerPaddingBottom: container.style.paddingBottom,
      containerMarginTop: container.style.marginTop,
      canvasWidth: canvas.style.width,
      canvasHeight: canvas.style.height,
      typography: this.snapshotChartTypography(chart),
      responsive: chart.options?.responsive,
      animation: chart.options?.animation,
      maintainAspectRatio: chart.options?.maintainAspectRatio,
      devicePixelRatio: chart.options?.devicePixelRatio,
      detachedForExport: false,
      exportCardOverflow: exportCard?.style.overflow ?? '',
      // y-axis beginAtZero: Chart.js applies this as a bar-chart type default at init, but
      // after chart.update('none') at export dimensions the auto-scale may override it.
      yBeginAtZero: chart.options?.scales?.['y']?.beginAtZero,
    };

    if (chart.options) {
      chart.options.responsive = false;
      chart.options.animation = false;
    }

    if (exportAnchor && parent) {
      parent.insertBefore(exportAnchor, container);
      document.body.appendChild(container);
      saved.detachedForExport = true;
      container.style.position = 'fixed';
      container.style.left = '-12000px';
      container.style.top = '0';
      container.style.zIndex = '-1';
      container.style.visibility = 'visible';
    }

    this.applyExportChartTypography(chart);

    // Do not scale barThickness: charts are re-rendered at export dimensions and inflating
    // thickness misaligns vertical bars relative to the category axis (alerts per day/hour).

    if (isCircularChart && chart.options) {
      chart.options.maintainAspectRatio = true;
      chart.options.devicePixelRatio = layoutOptions.devicePixelRatio ?? CHART_EXPORT_DEVICE_PIXEL_RATIO;
      container.style.padding = '0';
      container.style.paddingBottom = '0';
      container.style.marginTop = '0';
      container.style.background = '#e5e8ef';
      container.style.backgroundColor = '#e5e8ef';
    } else if (chart.options) {
      chart.options.devicePixelRatio = layoutOptions.devicePixelRatio ?? CHART_EXPORT_DEVICE_PIXEL_RATIO;
    }

    container.style.boxSizing = 'border-box';
    container.style.minWidth = `${layoutWidthPx}px`;
    container.style.maxWidth = `${layoutWidthPx}px`;
    container.style.width = `${layoutWidthPx}px`;
    container.style.height = `${layoutHeightPx}px`;
    canvas.style.width = `${layoutWidthPx}px`;
    canvas.style.height = `${layoutHeightPx}px`;
    if (isHorizontalBarChart) {
      container.style.overflow = 'visible';
      if (exportCard) {
        exportCard.style.overflow = 'visible';
      }
    }

    // For vertical bar charts force y-axis to always start at zero so bars anchor to the
    // x-axis regardless of the data range.  Without this, Chart.js may auto-scale the
    // y-axis to the data minimum at export dimensions, making bars appear to float.
    if (!isHorizontalBarChart && !isCircularChart && chart.options?.scales?.['y']) {
      chart.options.scales['y'].beginAtZero = true;
    }

    chart.resize();
    chart.update('none');
    this.flushChartPaint(chart);

    return () => {
      this.restoreChartTypography(chart, saved.typography);
      // Restore inline-style overrides FIRST so the container/canvas collapse back to
      // their on-screen layout BEFORE Chart.js's responsive resize re-reads dimensions.
      container.style.boxSizing = '';
      container.style.width = saved.containerWidth;
      container.style.height = saved.containerHeight;
      container.style.minWidth = saved.containerMinWidth;
      container.style.maxWidth = saved.containerMaxWidth;
      container.style.background = saved.containerBackground;
      container.style.backgroundColor = saved.containerBackgroundColor;
      container.style.position = saved.containerPosition;
      container.style.left = saved.containerLeft;
      container.style.top = saved.containerTop;
      container.style.zIndex = saved.containerZIndex;
      container.style.visibility = saved.containerVisibility;
      container.style.padding = saved.containerPadding;
      container.style.paddingBottom = saved.containerPaddingBottom;
      container.style.marginTop = saved.containerMarginTop;
      canvas.style.width = saved.canvasWidth;
      canvas.style.height = saved.canvasHeight;

      if (exportCard) {
        exportCard.style.overflow = saved.exportCardOverflow;
      }

      if (saved.detachedForExport && saved.parentElement) {
        saved.parentElement.insertBefore(container, exportAnchor);
        exportAnchor?.remove();
      }

      // Restore Chart.js options.  Always re-assign — even to undefined — so the singleton
      // config object is not permanently mutated by the export.  When refreshChart()
      // recreates the chart from the same singleton config later, it must see the original
      // values (e.g. animation = undefined restores default animation; devicePixelRatio
      // = undefined lets Chart.js fall back to window.devicePixelRatio).
      if (chart.options) {
        chart.options.responsive = saved.responsive;
        chart.options.animation = saved.animation;
        chart.options.maintainAspectRatio = saved.maintainAspectRatio;
        chart.options.devicePixelRatio = saved.devicePixelRatio;
      }

      // Restore y-axis beginAtZero (only matters if the config didn't already set it).
      if (!isHorizontalBarChart && !isCircularChart && chart.options?.scales?.['y']) {
        if (saved.yBeginAtZero !== undefined) {
          chart.options.scales['y'].beginAtZero = saved.yBeginAtZero;
        } else {
          delete chart.options.scales['y'].beginAtZero;
        }
      }

      // Return the canvas so buildChartSnapshot can look up whichever Chart instance is
      // currently registered after styles are restored (avoid stale closed-over refs).
      return canvas;
    };
  }

  private snapshotChartTypography(chart: ExportableChart): {
    xTicksFont?: unknown;
    yTicksFont?: unknown;
    legendFont?: unknown;
    datalabelsFont?: unknown;
  } {
    return {
      xTicksFont: chart.options?.scales?.x?.ticks?.font,
      yTicksFont: chart.options?.scales?.y?.ticks?.font,
      legendFont: chart.options?.plugins?.legend?.labels?.font,
      datalabelsFont: chart.options?.plugins?.datalabels?.font,
    };
  }

  /** Tick/legend size for chart canvas export (fixed ~11px, not width-scaled). */
  resolveChartExportFontSizePx(): number {
    return CHART_EXPORT_BASE_FONT_SIZE_PX;
  }

  private mergeChartFontSize(existing: unknown, sizePx: number): { size: number } & Record<string, unknown> {
    if (existing && typeof existing === 'object') {
      return { ...(existing as Record<string, unknown>), size: sizePx };
    }
    return { size: sizePx };
  }

  private applyExportChartTypography(chart: ExportableChart): void {
    const tickFontSize = this.resolveChartExportFontSizePx();
    const legendFontSize = Math.min(
      CHART_EXPORT_MAX_TICK_FONT_SIZE_PX,
      CHART_EXPORT_LEGEND_BASE_FONT_SIZE_PX
    );

    if (chart.options?.scales?.x?.ticks) {
      chart.options.scales.x.ticks.font = this.mergeChartFontSize(
        chart.options.scales.x.ticks.font,
        tickFontSize
      );
    }
    if (chart.options?.scales?.y?.ticks) {
      chart.options.scales.y.ticks.font = this.mergeChartFontSize(
        chart.options.scales.y.ticks.font,
        tickFontSize
      );
    }
    if (chart.options?.plugins?.legend?.labels) {
      chart.options.plugins.legend.labels.font = this.mergeChartFontSize(
        chart.options.plugins.legend.labels.font,
        legendFontSize
      );
    }
    if (chart.options?.plugins?.datalabels) {
      chart.options.plugins.datalabels.font = this.mergeChartFontSize(
        chart.options.plugins.datalabels.font,
        tickFontSize
      );
    }
  }

  private restoreChartTypography(
    chart: ExportableChart,
    saved: ReturnType<StatisticsExportCaptureService['snapshotChartTypography']>
  ): void {
    if (chart.options?.scales?.x?.ticks) {
      chart.options.scales.x.ticks.font = saved.xTicksFont;
    }
    if (chart.options?.scales?.y?.ticks) {
      chart.options.scales.y.ticks.font = saved.yTicksFont;
    }
    if (chart.options?.plugins?.legend?.labels) {
      chart.options.plugins.legend.labels.font = saved.legendFont;
    }
    if (chart.options?.plugins?.datalabels) {
      chart.options.plugins.datalabels.font = saved.datalabelsFont;
    }
  }

  /**
   * Row-height scale for tall horizontal exports (e.g. alert reasons).
   * Not used for tick font size — charts are already rendered at export width.
   */
  resolveChartExportTypographyScale(exportWidthPx: number = CHART_EXPORT_WIDTH_PX): number {
    return Math.min(
      CHART_EXPORT_MAX_TYPOGRAPHY_SCALE,
      exportWidthPx / CHART_EXPORT_BASELINE_WIDTH_PX
    );
  }

  private async waitForLayout(): Promise<void> {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  private flushChartPaint(chart: ExportableChart): void {
    const chartInstance = chart as ExportableChart & {
      draw?: () => void;
      render?: () => void;
    };
    chartInstance.draw?.();
    chartInstance.render?.();
  }

  private async waitForChartPaint(chart: ExportableChart | undefined): Promise<void> {
    await this.waitForLayout();
    if (!chart) {
      return;
    }

    this.flushChartPaint(chart);
    await this.waitForLayout();

    // All bar charts (vertical and horizontal) need extra time to finish axis and bar
    // layout calculations at the export canvas size before we sample pixels.
    // Dense datasets (>20 series) also need the extra settle time.
    const chartType = (chart as { config?: { type?: string } })?.config?.type;
    const isBarChart = chartType === 'bar';
    const isHorizontalBarChart = chart.options?.indexAxis === 'y';
    const datasetCount = chart.data?.datasets?.length ?? 0;
    if (isBarChart || isHorizontalBarChart || datasetCount > 20) {
      await new Promise<void>((resolve) => setTimeout(resolve, 100));
      this.flushChartPaint(chart);
      await this.waitForLayout();
    }
  }

  private renderCanvasWithBackground(
    sourceCanvas: HTMLCanvasElement,
    backgroundColor: string
  ): string {
    const canvas = document.createElement('canvas');
    canvas.width = sourceCanvas.width;
    canvas.height = sourceCanvas.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return sourceCanvas.toDataURL('image/png', 1);
    }

    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(sourceCanvas, 0, 0);
    const dataUrl = canvas.toDataURL('image/png');
    // Temp compositing canvas — release immediately (sourceCanvas is owned elsewhere).
    this.freeCanvas(canvas);
    return dataUrl;
  }
}

/**
 * Returns true when a pixel's colour is indistinguishable from the export
 * background (white or near-white) and should be excluded from content-area
 * trimming calculations.
 * Exported so StatisticsPdfExportService can share the same threshold logic.
 */
export function isExportBackgroundPixel(red: number, green: number, blue: number): boolean {
  const luminance = 0.299 * red + 0.587 * green + 0.114 * blue;
  if (luminance > 245) {
    return true;
  }
  return red < 15 && green > 15 && green < 40 && blue > 60 && blue < 100;
}
